import { useState, useEffect, useRef } from 'react';
import { Button, ButtonWidth, Checkbox, Drawer, useSession, useTranslation } from '@nextgen-web-framework/all';
import { StorageService } from '@nextgen-web-framework/core';
import {
  AmwayUserDetails,
  ApiProjections,
  CartConstantsEnum,
  CartEventsEnum,
  Cart,
  PublishEvent,
  cartApi,
  updateCart,
  type BundleSlot,
  ListResponse,
  ListEntry,
  CartEvents,
  CartEventsVersionsEnum,
  isGroupOrder,
  OrderType,
  getBundleEntriesAndChildPromoitions,
  CartEntryDetails,
  getBundleState,
  ListBundleSlot,
  type IBundleStateWithEntry,
  MyShopStorageConstant,
  AccountProcessType,
  type AmwayValue,
  getActiveCart,
  createOrFetchCart,
} from '@commerce-cart-webcomponents/sdk';
import {
  DerivedAvailabilityStatus,
  ProductData,
  productApi,
  productDataTransformer,
} from '@commerce-product-webcomponents/sdk';
import { useDispatch } from 'react-redux';
import { ProductCartEntryComponent } from '@commerce-product-webcomponents/ui-composite';
import { AdvanceShareCodeResponse } from '@commerce-webcomponents/sdk';
import { AccountType } from '@commerce-cart-webcomponents/sdk/services/models/AccountType';
import { useCartNotification } from '../../../../common/hooks/useCartNotification';
import { CartNotificationTheme } from '../../../../common/components/cart-notification/types/enums';
import { TOAST_TIMEOUT } from '../../../../common/types/enums';
import { AddToCartDrawer } from '../../../add-to-cart-drawer/AddToCartDrawer';
import { AccountSearchDrawer } from '../../../../common/components/account-search-drawer/AccountSearchDrawer';
import { ListSummaryLoaderMobile } from '../list-loaders/list-summary-loader/list-summary-loader-mobile/ListSummaryLoaderMobile';
import { disableProduct } from '../../../../utils/commonUtils';
import { DynamicBundleDrawer } from '../../../../common/components/dynamic-bundle-drawer/DynamicBundleDrawer';
import { AccountRegistrationBanner } from '../../../account-registration-banner/AccountRegistrationBanner';
import { useFeatureFlag } from '../../../../common';
import { MultipleItemDrawer } from './multiple-item-drawer/MultipleItemDrawer';
import styles from './AddListToCartDrawer.module.scss';
import { AddListToCartDrawerProps, IEntryObj } from './types/propsType';
import { MIN_VALUE_STEPPER } from './types/enums';

export const AddListToCartDrawer = ({ handleDrawerClose, myList, cart }: AddListToCartDrawerProps) => {
  const t = useTranslation();
  const dispatch = useDispatch();
  const subscriptionIds = useRef<string[]>();
  const [displayDrawerForm, setDisplayDrawerForm] = useState(false);
  const [displayMultipleItemDrawer, setDisplayMultipleItemDrawer] = useState(false);
  const { createToast, deleteToast } = useCartNotification('AddListToCartDrawer');
  const { isABO, isAnonymous } = useSession();
  const addToCartText = t('cart.addListToCart.addToCart');
  const [selectedAccount, setSelectedAccount] = useState<AmwayUserDetails>();
  const [myListData, setMyListData] = useState<ListResponse | null>(null);
  const { hideBVFlag } = useFeatureFlag();

  const [showBundleDrawer, setShowBundleDrawer] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const [productData, setProductData] = useState<ProductData>();
  const [bundleState, setBundleState] = useState<IBundleStateWithEntry[]>();
  const shareCode = StorageService.getLocalStorage().retrieve(
    MyShopStorageConstant.SHARECODEATTRIBUTES,
  ) as AdvanceShareCodeResponse;

  const [currentBundleEntry, setCurrentBundleEntry] = useState<ListEntry>();
  const activeCart = cart.id ? getActiveCart(cart, '') : {};

  const [checkedEntries, setCheckedEntries] = useState<ListEntry[]>([]);
  const [selectAllCheckbox, setSelectAllCheckbox] = useState(false);
  const drawerHeaderTitle = t('cart.addToCartDrawer.drawerHeaderTitle');
  const placeHolderText = t('cart.addToCartDrawer.placeHolderText');
  const accountCancelText = t('cart.addToCartDrawer.accountCancel');
  const accountAddCartText = t('cart.addToCartDrawer.accountAddCart');

  const addListToCartSuccessText = t('cart.addListToCart.addListToCartSuccess', {
    count: String(checkedEntries.reduce((acc, entry) => acc + entry.quantity, 0)),
  });

  const updateCartWithRegistrationProduct = async (cartId: string) => {
    if (isAnonymous() && shareCode.registrationAccountType) {
      const res = (await cartApi.updateCart(cartId, cartId, {
        accountProcess: {
          type: AccountProcessType.Registration,
          businessNature: shareCode.registrationAccountType as AccountType,
        },
        orderType: OrderType.Regular,
        version: +StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion),
      })) as Cart;
      dispatch(updateCart(res));
    }
  };

  const handleButtonClick = async () => {
    let fetchedOrCreatedCartId = '';
    if (checkedEntries.length > 1 && isGroupOrder(cart.orderType)) {
      setDisplayMultipleItemDrawer(true);
      return;
    }

    const selectedEntriesFromDrawer =
      checkedEntries
        ?.map(({ product, quantity, bundleSlots }) => ({
          product: { id: product.id },
          quantity,
          bundleSlots,
        }))
        .filter((entry) => !disableProduct(entry)) ?? [];

    const cartId = StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId);
    if (!cartId) {
      fetchedOrCreatedCartId = (await createOrFetchCart()).id;
    }

    const addToCartPayload = {
      subCarts: [
        {
          subCartId: fetchedOrCreatedCartId || activeCart.subCartId,
          entries: selectedEntriesFromDrawer as CartEntryDetails[],
        },
      ],
      orderType: cart.orderType,
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
    };

    try {
      const cartRes = (await cartApi.addToCart({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        subCartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        projections: ApiProjections.MIN,
        payload: addToCartPayload,
      })) as Cart;

      updateCartWithRegistrationProduct(cartRes.id);
      PublishEvent({
        cartEvent: CartEventsEnum.AddToCart,
        cart: cartRes,
      });
      PublishEvent({
        cartEvent: CartEventsEnum.UpdateMiniCartIcon,
        cart: cartRes,
      });
      createToast(
        {
          theme: CartNotificationTheme.SUCCESS,
          rightSeperator: false,
          content: addListToCartSuccessText,
          clickIcon: 'times',
          icon: 'check-circle',
          timeOut: TOAST_TIMEOUT,
        },
        deleteToast,
      );
    } finally {
      handleDrawerClose?.();
    }
  };

  function handleAddCart() {
    const payload = {
      version: Number(StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartReqVersion)),
      orderType: cart.orderType,
      entries: [],
      amwayUser: {
        amwayUserId: selectedAccount?.amwayUserId as string,
        accountId: selectedAccount?.accountId,
      },
    };

    cartApi
      .createSubCart({
        cartId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.CartId),
        payload,
      })
      .then((res) => {
        setDisplayDrawerForm(false);
        dispatch(updateCart(res as Cart));
        PublishEvent({
          cartEvent: CartEventsEnum.UpdateMiniCartIcon,
          cart: res as Cart,
        });
      });
  }

  const multipleItemDrawerclose = () => {
    setDisplayDrawerForm(false);
    setDisplayMultipleItemDrawer(false);
  };

  const updateQtyHandler = (entryObj: IEntryObj, qty: number) => {
    const updatedEntries = myListData?.payLoad?.entries?.map((entry: ListEntry) => {
      if (entry.entryId == entryObj.entryId) {
        return { ...entry, quantity: qty };
      }
      return entry;
    });

    const updatedCheckedEntries = checkedEntries.map((entry: ListEntry) => {
      if (entry.entryId == entryObj.entryId) {
        return { ...entry, quantity: qty };
      }
      return entry;
    });

    setCheckedEntries(updatedCheckedEntries);

    setMyListData({
      ...myListData,
      payLoad: {
        ...myListData?.payLoad,
        entries: updatedEntries,
      },
      totalEntries: updatedEntries?.reduce((total, currentEntry) => total + currentEntry.quantity, 0),
    });
  };

  useEffect(() => {
    setMyListData(myList);
  }, [myList]);

  useEffect(() => {
    if (!myListData) {
      return;
    }
  }, [myListData]);

  useEffect(() => {
    const showLoaderEvent = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.ShowLoader],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: () => {
        setShowLoader(true);
      },
    });

    const hideLoaderEvent = CartEvents.subscribeEvents({
      topics: [CartEventsEnum.HideLoader],
      version: CartEventsVersionsEnum['1.0.0'],
      cb: () => {
        setShowLoader(false);
      },
    });

    subscriptionIds.current = [...hideLoaderEvent, ...showLoaderEvent];

    return () => {
      CartEvents.unsubscribe(subscriptionIds.current);
    };
  }, []);

  function addToCartDrawerOrButton() {
    if (checkedEntries.length === 1 && isGroupOrder(cart.orderType)) {
      const [checkedEntry] = checkedEntries;
      const { product, quantity, bundleSlots, totalPrice } = checkedEntry;
      const { pointValue = '', businessVolume = '' } = totalPrice?.amwayValue || {};
      return (
        <AddToCartDrawer
          disabled={!checkedEntries.length}
          entryObj={{
            productId: product.id,
            quantity,
            bundleSlots: bundleSlots as BundleSlot[] | undefined,
            link: product.cartIconPublicUrl as string,
            alt: 'ProductDetail',
            brandName: product.brand,
            productName: product.name,
            productPrice: product.basePrice?.value,
            productSize: product.copyServingSize,
            pointValue: isABO() ? String(pointValue) : '',
            businessVolume: isABO() && !hideBVFlag ? String(businessVolume) : '',
          }}
        />
      );
    }

    return (
      <Button
        disabled={!checkedEntries.length}
        onClick={handleButtonClick}
        buttonWidth={ButtonWidth.FULL_WIDTH}
        title={addToCartText}
      />
    );
  }

  const handleAllCheckBox = () => {
    if (selectAllCheckbox) {
      setCheckedEntries([]);
      setSelectAllCheckbox((d) => !d);
      return;
    }
    const allEntries = myListData?.payLoad?.entries?.filter(
      (entry) => !disableCheckbox(entry.product.derivedAvailabilityStatus as string),
    );

    setCheckedEntries(allEntries as ListEntry[]);
    setSelectAllCheckbox((d) => !d);
  };

  function createFooter() {
    if (showLoader) {
      return (
        <ListSummaryLoaderMobile hideBoxShadow={true} className={styles['add-list-to-cart-drawer-footer-loader']} />
      );
    }

    return (
      <div className={styles['add-list-to-cart-drawer-footer']}>
        <div className={styles['add-list-to-cart-drawer-footer-total-section']}>
          <Checkbox checked={selectAllCheckbox} onClick={handleAllCheckBox} text={t('cart.addListToCart.all')} />
        </div>
        {addToCartDrawerOrButton()}
      </div>
    );
  }

  async function handleModifySelection(productId: string, bundleEntry: ListEntry) {
    const res = await productApi.getProductByIdOrError({
      productId,
      warehouseId: StorageService.getLocalStorage().retrieve(CartConstantsEnum.WarehouseId),
    });

    const budleStateStored = getBundleState(bundleEntry.bundleSlots as ListBundleSlot.BundleSlot[]);
    setBundleState(budleStateStored);
    if (!(res instanceof Response)) {
      setProductData(res);
      setShowBundleDrawer(true);
      setCurrentBundleEntry(bundleEntry);
    }
  }

  const disableCheckbox = (stockStatus: string) =>
    (
      [DerivedAvailabilityStatus.Nla, DerivedAvailabilityStatus.OutOfStock, DerivedAvailabilityStatus.Nya] as string[]
    ).includes(stockStatus);

  const handleCheckBoxChange = (entry: ListEntry) => {
    if (disableCheckbox(entry.product.derivedAvailabilityStatus as string)) {
      return;
    }
    setSelectAllCheckbox(false);
    const index = checkedEntries.findIndex(({ entryId }) => entryId === entry.entryId);
    if (index !== -1) {
      setCheckedEntries((prevEntries) => {
        const clonedEntries = [...prevEntries];
        clonedEntries.splice(index, 1);
        return clonedEntries;
      });
      return;
    }
    setCheckedEntries((prevEntries) => {
      const updatedEntries = prevEntries.concat(entry);
      const listEntries = myListData?.payLoad?.entries?.filter(
        (entry) => !disableCheckbox(entry.product.derivedAvailabilityStatus as string),
      );

      updatedEntries.length === listEntries?.length && setSelectAllCheckbox(true);
      return updatedEntries;
    });
  };

  function showDrawers() {
    if (showBundleDrawer) {
      return (
        <DynamicBundleDrawer
          bundleEntryDetails={currentBundleEntry}
          onClose={() => setShowBundleDrawer(false)}
          storedState={bundleState}
          productData={productDataTransformer(productData as ProductData)}
        />
      );
    }
    if (displayDrawerForm) {
      return (
        <AccountSearchDrawer
          cartData={cart}
          onPrimaryButtonClick={handleDrawerClose}
          onSecondaryClick={handleAddCart}
          drawerHeaderTitle={drawerHeaderTitle}
          placeHolderText={placeHolderText}
          primaryButtonText={accountCancelText}
          secondaryButtonText={accountAddCartText}
          onRadioChange={({ amwayUserId, accountId, firstName, lastName }) => {
            setSelectedAccount({
              amwayUserId,
              accountId,
              firstName,
              lastName,
            });
          }}
        />
      );
    }
    if (displayMultipleItemDrawer) {
      return (
        <MultipleItemDrawer
          imageURL={myListData?.payLoad?.entries?.[0]?.product.cartIconPublicUrl}
          listEntries={checkedEntries}
          onClose={multipleItemDrawerclose}
          onSearchClick={() => setDisplayDrawerForm(true)}
          onConfirmSuccess={handleDrawerClose}
          onOrderTypeChange={() => setDisplayMultipleItemDrawer(false)}
        />
      );
    }

    return (
      <Drawer
        header={addToCartText}
        bottomGap={0}
        closeIcon={true}
        closeOnBackdropClick={true}
        onClose={handleDrawerClose}
        footer={createFooter()}
        useReactPortal={true}
        portalContainerId={'react-portal-drawer'}
      >
        <div className={styles['add-list-to-cart-drawer-product-entry']}>
          <AccountRegistrationBanner isListBanner={true} />
          {myListData?.payLoad?.entries?.map((entry) => {
            const { product, retailPrice, quantity } = entry;
            const { pointValue, businessVolume } = retailPrice?.amwayValue ?? ({} as AmwayValue);
            return (
              <div key={entry.entryId} className={styles['add-list-to-cart-drawer-product-entry-card']}>
                <ProductCartEntryComponent
                  forceMobileView={true}
                  initialCheckBoxVal={!!checkedEntries.find((d) => d.entryId === entry.entryId)}
                  onRemoveProduct={() => null}
                  isCheckBoxDisabled={disableCheckbox(product.derivedAvailabilityStatus as string)}
                  onModifyBundleButtonClick={() => handleModifySelection(product.id, entry)}
                  onCheckboxClick={() => handleCheckBoxChange(entry)}
                  data={{
                    productId: product.id,
                    link: product.cartIconPublicUrl as string,
                    alt: product.cartIconPublicUrl as string,
                    brandName: product.brand as string,
                    bundle: getBundleEntriesAndChildPromoitions(entry as CartEntryDetails)?.bundleEntries,
                    productName: product.name ?? '',
                    productSize: product.copyServingSize as string,
                    pointValue: String(isABO() && pointValue && pointValue * quantity),
                    businessVolume: String(isABO() && !hideBVFlag && businessVolume && businessVolume * quantity),
                    itemDisposition: '',
                    entryId: entry?.entryId,
                    productPrice: (retailPrice?.value ?? 0) * quantity,
                    disabled: disableProduct(entry),
                    items: [],
                  }}
                  showPropertiesCollection={{
                    showModifyBundleButton: true,
                    showCheckBox: true,
                    showCloseCta: false,
                    showRemoveIcon: false,
                    showCancelIcon: false,
                  }}
                  cartBaseurl={''}
                  minValueForStepper={MIN_VALUE_STEPPER}
                  updateQtyCustomHandler={updateQtyHandler}
                />
              </div>
            );
          })}
        </div>
      </Drawer>
    );
  }

  return <>{showDrawers()}</>;
};
